import type {
  FiscalCompanyType,
  FiscalTaxType,
  IraeFrequency,
} from "@/services/fiscalService";

export interface IndustryOption {
  value: string;
  labelKey: string;
}

export interface IndustrySection {
  sectionKey: string;
  options: IndustryOption[];
}

export const INDUSTRY_SECTIONS: IndustrySection[] = [
  {
    sectionKey: "servicios",
    options: [
      { value: "professional_services", labelKey: "professional_services" },
      { value: "health_professionals", labelKey: "health_professionals" },
      { value: "education_training", labelKey: "education_training" },
      { value: "legal_accounting_services", labelKey: "legal_accounting_services" },
      { value: "other_services", labelKey: "other_services" },
    ],
  },
  {
    sectionKey: "comercio",
    options: [
      { value: "retail_physical", labelKey: "retail_physical" },
      { value: "ecommerce", labelKey: "ecommerce" },
      { value: "wholesale_distribution", labelKey: "wholesale_distribution" },
      { value: "import_resale", labelKey: "import_resale" },
    ],
  },
  {
    sectionKey: "industria_produccion",
    options: [
      { value: "manufacturing", labelKey: "manufacturing" },
      { value: "food_beverage_production", labelKey: "food_beverage_production" },
      { value: "construction", labelKey: "construction" },
      { value: "industrial_services", labelKey: "industrial_services" },
    ],
  },
  {
    sectionKey: "exportacion",
    options: [
      { value: "services_export", labelKey: "services_export" },
      { value: "goods_export", labelKey: "goods_export" },
      { value: "mixed_export", labelKey: "mixed_export" },
    ],
  },
  {
    sectionKey: "tecnologia_software",
    options: [
      { value: "saas_software_development", labelKey: "saas_software_development" },
      { value: "digital_products", labelKey: "digital_products" },
      { value: "platforms_marketplaces", labelKey: "platforms_marketplaces" },
    ],
  },
  {
    sectionKey: "unipersonal_freelancer",
    options: [
      { value: "independent_professional", labelKey: "independent_professional" },
      { value: "small_services", labelKey: "small_services" },
      { value: "side_business", labelKey: "side_business" },
    ],
  },
  {
    sectionKey: "inmobiliario",
    options: [
      { value: "rental_income", labelKey: "rental_income" },
      { value: "property_development", labelKey: "property_development" },
      { value: "construction_projects", labelKey: "construction_projects" },
    ],
  },
  {
    sectionKey: "agro_rural",
    options: [
      { value: "farming", labelKey: "farming" },
      { value: "livestock", labelKey: "livestock" },
      { value: "agricultural_services", labelKey: "agricultural_services" },
    ],
  },
  {
    sectionKey: "finanzas_inversiones",
    options: [
      { value: "holding_company", labelKey: "holding_company" },
      { value: "investments", labelKey: "investments" },
      { value: "financial_services", labelKey: "financial_services" },
    ],
  },
  {
    sectionKey: "other",
    options: [{ value: "other", labelKey: "other" }],
  },
];

/**
 * Deliberately no ivaRegime / ivaFrequency here. DGI assigns the CEDE / NO CEDE
 * grupo by turnover and pequeña-empresa (IVA mínimo) status by revenue ceiling —
 * neither is a property of what the business does, so no per-industry value can
 * be right. Every industry used to default to ivaRegime "minimum" paired with
 * ivaFrequency "monthly": incoherent on its own terms (IVA mínimo is the
 * pequeña-empresa regime, defaulted alongside companyType "sas" + employees +
 * IRAE) and already at odds with ivaFrequencyForRegime, which maps minimum to
 * "minimum". The tenant declares the regime; we do not guess it from a label.
 */
export interface IndustryFiscalDefaults {
  companyType?: FiscalCompanyType;
  isExporter?: boolean;
  hasEmployees?: boolean;
  taxes?: FiscalTaxType[];
  iraeFrequency?: IraeFrequency;
}

export const INDUSTRY_FISCAL_DEFAULTS: Record<string, IndustryFiscalDefaults> = {
  professional_services: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  health_professionals: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  education_training: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  legal_accounting_services: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  other_services: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  retail_physical: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  ecommerce: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  wholesale_distribution: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  import_resale: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  manufacturing: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  food_beverage_production: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  construction: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  industrial_services: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  services_export: {
    companyType: "sas",
    isExporter: true,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  goods_export: {
    companyType: "sas",
    isExporter: true,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  mixed_export: {
    companyType: "sas",
    isExporter: true,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  saas_software_development: {
    companyType: "sas",
    isExporter: true,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  digital_products: {
    companyType: "sas",
    isExporter: true,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  platforms_marketplaces: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  independent_professional: {
    companyType: "unipersonal",
    isExporter: false,
    hasEmployees: false,
    taxes: ["IVA", "BPS"],
    iraeFrequency: "annual",
  },
  small_services: {
    companyType: "unipersonal",
    isExporter: false,
    hasEmployees: false,
    taxes: ["IVA", "BPS"],
    iraeFrequency: "annual",
  },
  side_business: {
    companyType: "unipersonal",
    isExporter: false,
    hasEmployees: false,
    taxes: ["IVA", "BPS"],
    iraeFrequency: "annual",
  },
  rental_income: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: false,
    taxes: ["IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  property_development: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  construction_projects: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  farming: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  livestock: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  agricultural_services: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  holding_company: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: false,
    taxes: ["IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  investments: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: false,
    taxes: ["IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  financial_services: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
  other: {
    companyType: "sas",
    isExporter: false,
    hasEmployees: true,
    taxes: ["IVA", "IRAE", "BPS"],
    iraeFrequency: "annual",
  },
};

export function getAllIndustryOptions(): IndustryOption[] {
  return INDUSTRY_SECTIONS.flatMap((s) => s.options);
}
